interface Props {
  visible: boolean;
  onClose: () => void;
  onNavigate?: (target: 'scoring' | 'transparency') => void;
  onOpenDoc?: (doc: 'privacy' | 'terms' | 'subprocessors') => void;
}

export default function LegalNotice({ visible, onClose, onNavigate, onOpenDoc }: Props) {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-ink/60 backdrop-blur-md p-4">
      <div className="panel-ink rounded-[26px] border-2 border-ink max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/12">
          <h2 className="font-display text-lg text-white">
            Mentions l&eacute;gales et avertissement
          </h2>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5 text-sm text-white/65 leading-relaxed">

          <section>
            <h3 className="font-display text-white mb-2">
              &Eacute;diteur du site
            </h3>
            <ul className="space-y-0.5 pl-4 list-disc">
              <li>Ludovic Mornand, exer&ccedil;ant en Entreprise Individuelle</li>
              <li>Si&egrave;ge social&nbsp;: 3 rue Pierre Valdo, 69005 Lyon, France</li>
              <li>SIREN&nbsp;: 513&nbsp;040&nbsp;576 &mdash; SIRET&nbsp;: 513&nbsp;040&nbsp;576&nbsp;00052</li>
              <li>TVA intracommunautaire&nbsp;: FR42&nbsp;513040576</li>
              <li>Directeur de la publication&nbsp;: Ludovic Mornand</li>
              <li>Contact&nbsp;: <a href="mailto:contact@blackthornsdesign.com" className="underline text-rose-pastel-400">contact@blackthornsdesign.com</a></li>
              <li>Adresse de production&nbsp;: <a href="https://app.visaly.fr" target="_blank" rel="noreferrer" className="underline text-rose-pastel-400">app.visaly.fr</a></li>
            </ul>
          </section>

          <section>
            <h3 className="font-display text-white mb-2">
              H&eacute;bergement
            </h3>
            <p>
              Le site est h&eacute;berg&eacute; par <strong>IONOS SARL</strong>, 7 place de la Gare, 57200 Sarreguemines, France. Infrastructure et donn&eacute;es de l'application h&eacute;berg&eacute;es en Union europ&eacute;enne.
            </p>
            <p className="mt-2">
              La base de donn&eacute;es et le stockage des fichiers utilisateurs sont g&eacute;r&eacute;s par <strong>Supabase</strong> en r&eacute;gion Paris (eu-west-3). Les mod&egrave;les d'intelligence artificielle utilis&eacute;s pour l'analyse sont fournis par <strong>Mistral AI</strong>, soci&eacute;t&eacute; fran&ccedil;aise, h&eacute;berg&eacute;s en Union europ&eacute;enne.
            </p>
          </section>

          <section>
            <h3 className="font-display text-white mb-2">
              Nature du service
            </h3>
            <p>
              Visaly est un outil d'aide &agrave; la d&eacute;cision utilisant l'intelligence
              artificielle pour pr&eacute;-scorer la conformit&eacute; de contenus publicitaires &agrave;
              la r&eacute;glementation fran&ccedil;aise encadrant la publicit&eacute; des produits de
              sant&eacute; &mdash; m&eacute;dicaments (articles L5122-1 et suivants, R5122-1 et
              suivants du Code de la sant&eacute; publique, visas GP/PM d&eacute;livr&eacute;s par
              l'ANSM), compl&eacute;ments alimentaires, cosm&eacute;tiques et dispositifs
              m&eacute;dicaux.
            </p>
            <p className="mt-2">
              Visaly est un outil d'aide &agrave; la d&eacute;cision. Il ne remplace ni la revue
              r&eacute;glementaire du laboratoire, ni le visa de l'ANSM, ni un avis juridique.
              Visaly est un service ind&eacute;pendant&nbsp;: il n'est ni affili&eacute; &agrave;
              l'ANSM, ni approuv&eacute; par elle.
            </p>
          </section>

          <section>
            <h3 className="font-display text-white mb-2">
              Absence de valeur juridique
            </h3>
            <p>
              Les analyses,{' '}
              {onNavigate ? (
                <button
                  onClick={() => { onClose(); onNavigate('scoring'); }}
                  className="underline text-rose-pastel-400 hover:text-rose-pastel-300 transition-colors"
                >
                  scores et recommandations
                </button>
              ) : (
                'scores et recommandations'
              )}{' '}
              produits par Visaly sont
              fournis <strong>&agrave; titre purement indicatif</strong>. Ils ne constituent en aucun
              cas un avis juridique, une consultation d'avocat, ni une garantie de conformit&eacute;
              l&eacute;gale. Aucun r&eacute;sultat g&eacute;n&eacute;r&eacute; par l'application ne
              saurait se substituer &agrave; l'expertise d'un professionnel du droit.
            </p>
          </section>

          <section>
            <h3 className="font-display text-white mb-2">
              Obligation de validation par un professionnel
            </h3>
            <p>
              Toute d&eacute;cision li&eacute;e &agrave; la publication, la diffusion ou la
              modification d'un contenu publicitaire portant sur des produits de sant&eacute;
              doit &ecirc;tre valid&eacute;e par un professionnel qualifi&eacute; (affaires
              r&eacute;glementaires, pharmacien responsable, avocat sp&eacute;cialis&eacute; en droit
              de la sant&eacute;) et, lorsque le r&eacute;gime l'exige, faire l'objet du visa
              pr&eacute;alable de l'ANSM. Cela s'applique quel que soit votre statut :
              laboratoire, agence de communication, influenceur, distributeur ou tout autre
              acteur de la fili&egrave;re.
            </p>
          </section>

          <section>
            <h3 className="font-display text-white mb-2">
              Limitation de responsabilit&eacute;
            </h3>
            <p>
              L'&eacute;diteur de Visaly ne pourra en aucun cas &ecirc;tre tenu responsable des
              cons&eacute;quences directes ou indirectes r&eacute;sultant de l'utilisation des
              r&eacute;sultats fournis par l'application, y compris en cas de poursuites judiciaires,
              de sanctions administratives, ou de tout autre pr&eacute;judice li&eacute; &agrave; la
              non-conformit&eacute; d'un contenu &agrave; la l&eacute;gislation en vigueur.
            </p>
            <p className="mt-2">
              L'utilisateur assume l'enti&egrave;re responsabilit&eacute; de l'usage qu'il fait des
              analyses et recommandations fournies par Visaly.
            </p>
          </section>

          <section>
            <h3 className="font-display text-white mb-2">
              Donn&eacute;es juridiques
            </h3>
            <p>
              Les textes de loi et jurisprudences cit&eacute;s dans les r&eacute;sultats proviennent
              de l'API L&eacute;gifrance, mise &agrave; disposition par la DILA (Direction de
              l'information l&eacute;gale et administrative) sous{' '}
              <a
                href="https://www.etalab.gouv.fr/wp-content/uploads/2017/04/ETALAB-Licence-Ouverte-v2.0.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-rose-pastel-400"
              >
                Licence Ouverte 2.0
              </a>.
            </p>
            <p className="mt-2">
              Ces donn&eacute;es sont fournies &agrave; titre informatif et <strong>ne sont pas
              juridiquement opposables</strong>. Seuls les textes publi&eacute;s au Journal officiel
              de la R&eacute;publique fran&ccedil;aise, dans leur version PDF sign&eacute;e, font foi.
            </p>
          </section>

          <section>
            <h3 className="font-display text-white mb-2">
              Intelligence artificielle
            </h3>
            <p>
              Visaly utilise des{' '}
              {onNavigate ? (
                <button
                  onClick={() => { onClose(); onNavigate('transparency'); }}
                  className="underline text-rose-pastel-400 hover:text-rose-pastel-300 transition-colors"
                >
                  mod&egrave;les de langage (IA)
                </button>
              ) : (
                'mod\u00e8les de langage (IA)'
              )}{' '}
              pour produire ses analyses. Ces
              mod&egrave;les, bien qu'avanc&eacute;s, peuvent commettre des erreurs
              d'interpr&eacute;tation, des omissions ou des inexactitudes. Les r&eacute;sultats
              doivent toujours &ecirc;tre examin&eacute;s avec discernement et recoup&eacute;s avec
              l'avis d'un professionnel qualifi&eacute;.
            </p>
          </section>

          {onOpenDoc && (
            <section className="border-t border-white/12 pt-4">
              <h3 className="font-display text-white mb-2">
                Documents juridiques
              </h3>
              <p className="mb-3">
                Pour plus de d&eacute;tails sur vos droits, les donn&eacute;es trait&eacute;es et les conditions
                d'utilisation du service, consultez les documents suivants&nbsp;:
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { onClose(); onOpenDoc('privacy'); }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-white/12 text-white/75 hover:bg-white/10 transition-colors"
                >
                  Politique de confidentialit&eacute;
                </button>
                <button
                  onClick={() => { onClose(); onOpenDoc('terms'); }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-white/12 text-white/75 hover:bg-white/10 transition-colors"
                >
                  Conditions G&eacute;n&eacute;rales d'Utilisation
                </button>
                <button
                  onClick={() => { onClose(); onOpenDoc('subprocessors'); }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-white/12 text-white/75 hover:bg-white/10 transition-colors"
                >
                  Sous-traitants
                </button>
              </div>
            </section>
          )}

        </div>

        <div className="p-6 border-t border-white/12">
          <button
            onClick={onClose}
            className="font-display w-full rounded-full bg-white py-3 text-ink transition-transform hover:scale-[1.01]"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
